import type { BlockPhysics } from './collision';
import { PLAYER_TOTAL_HEIGHT } from './controller';

export interface NavPoint {
  x: number;
  z: number;
}

const CELL_SIZE = 1; // meters — fine enough to route around building corners/the alley mouth without visible grid-snapping once string-pulled
const BOUNDS = { minX: -30, maxX: 30, minZ: -24, maxZ: 32 }; // covers every street-level habitat (buildings, alley, park, curbs) — see the class comment for why rooftop/tunnel/subway are deliberately excluded
const STREET_HEIGHT_TOLERANCE = 0.05; // groundHeightAt must resolve to essentially y=0 — excludes ramps/rooftop/tunnel/subway/vault-prop-tops, all of which sit meaningfully above or below true street level
// S10: rat_king (0.85, ai/archetypes.ts) replaced elite_rat (0.6) as the
// largest GROUND archetype's real footprint. Widening this constant to
// match (0.85+margin) was tried and REVERTED — it's this project's own
// "one derived number, re-check every downstream use" lesson (Classic
// gotcha #19) cutting the other way: a bigger conservative radius shrinks
// walkable space for EVERY ground archetype baked against this ONE shared
// grid, and measured live, it broke an existing, previously-passing
// harness check ("a ground enemy routes around a small obstacle" — the
// routed enemy ended up 3.8m from target instead of 2.9m, materially worse
// navigation for the common rat/pizza_rat/trash_lobber roster every real
// fight actually uses). Deliberately NOT widened: `rat_king`'s own real
// 0.85m collision radius (used directly by directGroundSeek's own
// resolveHorizontal call, independent of this baked grid) can in principle
// suggest a waypoint slightly tighter than its true silhouette needs —
// accepted because (a) elite_patrol's own habitat is open street, not a
// tight alley/prop cluster, and (b) even a bad waypoint can't make the Rat
// King clip through geometry (resolveHorizontal is the actual collision
// authority; NavGrid only proposes where to aim for) — worst case is an
// occasional stall against a corner it'd otherwise route cleanly around,
// far cheaper than degrading every other enemy's pathing to avoid it.
const AGENT_CLEARANCE_RADIUS = 0.65; // the largest COMMON ground archetype's radius (rat 0.35/pizza_rat 0.38/trash_lobber 0.45 are all well inside this) plus a small margin — baking one conservative grid keeps every ground archetype safely inside it, rather than maintaining one grid per radius
const AGENT_CLEARANCE_HEIGHT = PLAYER_TOTAL_HEIGHT; // matches the headY every ground archetype's own resolveHorizontal call already uses (ai/enemy.ts) — S12 gate-pass fix: this was STILL a bare `1.4` literal after ai/enemy.ts's own two call sites were converted to import the real constant (don't-break-list review catch — the exact "grep every OTHER hardcoded copy, not just the sites you already know about" lesson this project's own gotcha list keeps re-learning)
const MAX_SNAP_RING = 8; // generous search radius (in cells) for nearestWalkable — 8m is far more than any query point should ever legitimately sit off-grid by

/**
 * A min-heap keyed by fScore, with lazy deletion for the "decrease-key" case
 * (push a fresher, lower-f entry for an already-queued cell rather than
 * mutating in place; `findPath` skips a popped entry once its cell is
 * already `closed`). Kept private/local — nothing outside this file needs a
 * general-purpose heap.
 */
class MinHeap {
  private readonly items: Array<{ key: number; f: number }> = [];

  push(key: number, f: number): void {
    this.items.push({ key, f });
    let i = this.items.length - 1;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (this.items[parent].f <= this.items[i].f) break;
      [this.items[parent], this.items[i]] = [this.items[i], this.items[parent]];
      i = parent;
    }
  }

  pop(): { key: number; f: number } | undefined {
    const top = this.items[0];
    if (top === undefined) return undefined;
    const last = this.items.pop()!;
    if (this.items.length > 0) {
      this.items[0] = last;
      let i = 0;
      for (;;) {
        const l = i * 2 + 1;
        const r = i * 2 + 2;
        let smallest = i;
        if (l < this.items.length && this.items[l].f < this.items[smallest].f) smallest = l;
        if (r < this.items.length && this.items[r].f < this.items[smallest].f) smallest = r;
        if (smallest === i) break;
        [this.items[smallest], this.items[i]] = [this.items[i], this.items[smallest]];
        i = smallest;
      }
    }
    return top;
  }

  get size(): number {
    return this.items.length;
  }
}

/**
 * A walkable graph baked once from `BlockPhysics`'s floors + walls (PLAN-CITY
 * §5's S03 scope: "walkable graph baked from colliders"). Deliberately
 * scoped to STREET-LEVEL ground only (street, sidewalks, the alley, the park
 * pocket) — the exact area S02's spawner already restricted ground enemies
 * to ("kept off the rooftop/tunnel/subway... wandering onto a ramp they
 * can't navigate back off of would just get them stuck", ai/spawner.ts's own
 * former comment). Flyers (pigeon/dive-pigeon) never consult this at all —
 * they move through open air via their own steering in ai/enemy.ts, gated on
 * `BlockPhysics.resolveHorizontal`/`segmentClear` directly, not a grid.
 * Extending this graph down into the tunnel/subway or up onto the rooftop
 * for GROUND enemies is real future scope (a second habitat tier) — no
 * habitat spawns a ground archetype there yet (ai/habitats.ts), so there's
 * nothing this grid would currently be asked to route through anyway.
 */
export class NavGrid {
  private readonly cols: number;
  private readonly rows: number;
  private readonly walkable: Uint8Array;

  constructor(physics: BlockPhysics) {
    this.cols = Math.round((BOUNDS.maxX - BOUNDS.minX) / CELL_SIZE) + 1;
    this.rows = Math.round((BOUNDS.maxZ - BOUNDS.minZ) / CELL_SIZE) + 1;
    this.walkable = new Uint8Array(this.cols * this.rows);
    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        const x = BOUNDS.minX + col * CELL_SIZE;
        const z = BOUNDS.minZ + row * CELL_SIZE;
        const ground = physics.groundHeightAt(x, z, 0, STREET_HEIGHT_TOLERANCE);
        const onStreet = ground !== null && Math.abs(ground) <= STREET_HEIGHT_TOLERANCE;
        const clear = onStreet && !physics.isPointBlocked(x, z, AGENT_CLEARANCE_RADIUS, 0, AGENT_CLEARANCE_HEIGHT);
        this.walkable[row * this.cols + col] = clear ? 1 : 0;
      }
    }
  }

  private colOf(x: number): number {
    return Math.round((x - BOUNDS.minX) / CELL_SIZE);
  }
  private rowOf(z: number): number {
    return Math.round((z - BOUNDS.minZ) / CELL_SIZE);
  }
  private inBounds(col: number, row: number): boolean {
    return col >= 0 && col < this.cols && row >= 0 && row < this.rows;
  }
  private isWalkable(col: number, row: number): boolean {
    return this.inBounds(col, row) && this.walkable[row * this.cols + col] === 1;
  }

  /** Nearest walkable cell to (x,z), searching outward ring by ring — snaps an off-grid query point (an enemy standing a few cm off-grid, or just outside BOUNDS) onto the graph. Null only if nothing walkable exists within MAX_SNAP_RING cells. */
  private nearestWalkable(x: number, z: number): { col: number; row: number } | null {
    const col0 = this.colOf(x);
    const row0 = this.rowOf(z);
    if (this.isWalkable(col0, row0)) return { col: col0, row: row0 };
    for (let ring = 1; ring <= MAX_SNAP_RING; ring++) {
      for (let dc = -ring; dc <= ring; dc++) {
        for (let dr = -ring; dr <= ring; dr++) {
          if (Math.max(Math.abs(dc), Math.abs(dr)) !== ring) continue; // only this ring's own perimeter — interior cells were already checked at a smaller ring
          const col = col0 + dc;
          const row = row0 + dr;
          if (this.isWalkable(col, row)) return { col, row };
        }
      }
    }
    return null;
  }

  /**
   * A* over the 8-connected grid (diagonal moves only when BOTH orthogonal
   * neighbors are also walkable — otherwise a diagonal step can cut through
   * the corner of a wall spanning two adjacent cells, a classic grid-nav
   * bug). Returns a string-pulled waypoint list (see `simplify`), or null if
   * either endpoint has no reachable walkable cell nearby, or no path exists
   * between them (a genuinely disconnected pocket).
   */
  findPath(physics: BlockPhysics, fromX: number, fromZ: number, toX: number, toZ: number): NavPoint[] | null {
    const start = this.nearestWalkable(fromX, fromZ);
    const goal = this.nearestWalkable(toX, toZ);
    if (!start || !goal) return null;

    const startKey = start.row * this.cols + start.col;
    const goalKey = goal.row * this.cols + goal.col;
    if (startKey === goalKey) return [{ x: toX, z: toZ }];

    const heuristic = (col: number, row: number): number => Math.hypot(col - goal.col, row - goal.row);
    const gScore = new Map<number, number>([[startKey, 0]]);
    const cameFrom = new Map<number, number>();
    const closed = new Set<number>();
    const heap = new MinHeap();
    heap.push(startKey, heuristic(start.col, start.row));

    const MAX_EXPANSIONS = this.cols * this.rows; // a hard upper bound — every cell can be expanded at most once (the `closed` gate) regardless of grid size
    let expansions = 0;
    let found = false;

    while (heap.size > 0 && expansions < MAX_EXPANSIONS) {
      const top = heap.pop()!;
      if (closed.has(top.key)) continue; // a stale, already-superseded queue entry from before a cheaper path to this cell was found
      closed.add(top.key);
      expansions++;
      if (top.key === goalKey) {
        found = true;
        break;
      }
      const curCol = top.key % this.cols;
      const curRow = Math.floor(top.key / this.cols);

      for (let dc = -1; dc <= 1; dc++) {
        for (let dr = -1; dr <= 1; dr++) {
          if (dc === 0 && dr === 0) continue;
          const nCol = curCol + dc;
          const nRow = curRow + dr;
          if (!this.isWalkable(nCol, nRow)) continue;
          if (dc !== 0 && dr !== 0 && (!this.isWalkable(curCol + dc, curRow) || !this.isWalkable(curCol, curRow + dr))) continue; // no corner-cutting
          const neighborKey = nRow * this.cols + nCol;
          if (closed.has(neighborKey)) continue;
          const stepCost = dc !== 0 && dr !== 0 ? Math.SQRT2 : 1;
          const tentativeG = (gScore.get(top.key) ?? Infinity) + stepCost;
          if (tentativeG < (gScore.get(neighborKey) ?? Infinity)) {
            cameFrom.set(neighborKey, top.key);
            gScore.set(neighborKey, tentativeG);
            heap.push(neighborKey, tentativeG + heuristic(nCol, nRow));
          }
        }
      }
    }

    if (!found) return null;

    const rawCells: NavPoint[] = [];
    let cursor: number | undefined = goalKey;
    while (cursor !== undefined) {
      const col = cursor % this.cols;
      const row = Math.floor(cursor / this.cols);
      rawCells.unshift({ x: BOUNDS.minX + col * CELL_SIZE, z: BOUNDS.minZ + row * CELL_SIZE });
      cursor = cameFrom.get(cursor);
    }
    rawCells.push({ x: toX, z: toZ }); // the real target, not just its snapped cell — keeps the enemy's final approach exact rather than stopping at a cell center

    return this.simplify(physics, rawCells);
  }

  /** Greedy line-of-sight string-pulling: from each kept waypoint, jump to the farthest LATER waypoint still directly reachable (`BlockPhysics.segmentClear` at street height), dropping everything in between. A raw grid path reads as visibly robotic (right-angle zigzags) even when it's technically correct — this is what turns it into the fewest straight legs that still avoid every wall. */
  private simplify(physics: BlockPhysics, raw: NavPoint[]): NavPoint[] {
    if (raw.length <= 2) return raw;
    const out: NavPoint[] = [raw[0]];
    let anchor = 0;
    while (anchor < raw.length - 1) {
      let farthest = anchor + 1;
      for (let i = raw.length - 1; i > anchor; i--) {
        if (physics.segmentClear(raw[anchor].x, raw[anchor].z, raw[i].x, raw[i].z, 0.5)) {
          farthest = i;
          break;
        }
      }
      out.push(raw[farthest]);
      anchor = farthest;
    }
    return out;
  }
}
